import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { LoadingButton } from "@mui/lab";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Save as SaveIcon,
  WhatsApp as WhatsAppIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import DeliveryCenterCostsService from "../../../api/DeliveryCenter/deliveryCenterCosts";
import DeliveryCenterService from "../../../api/DeliveryCenter/deliveryCenter";

const ACCENT = "#059669";
const ACCENT_LIGHT = "#10b981";
const ACCENT_DARK = "#047857";
const ACCENT_BG = "#064e3b";

const WhatsAppPhoneModal = ({
  isOpen,
  onClose,
  editMode,
  initialData,
  onSuccess,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [formData, setFormData] = useState({
    countryCode: "55",
    areaCode: "",
    phoneNumber: "",
    deliveryCenterId: "",
  });

  const [deliveryCenters, setDeliveryCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isOpen) {
      fetchDeliveryCenters();
      initializeForm();
    }
  }, [isOpen, initialData]);

  const fetchDeliveryCenters = async () => {
    try {
      const response = await DeliveryCenterService.getDeliveryCenter();
      const centersData = response.data;
      setDeliveryCenters(centersData);
    } catch (error) {
      console.error("Erro ao buscar centros de distribuicao:", error);
      setError("Falha ao carregar centros de distribuicao.");
    }
  };

  const initializeForm = () => {
    if (editMode && initialData) {
      setFormData({
        countryCode: (initialData.countryCode || "55").replace(/\D/g, "") || "55",
        areaCode: initialData.areaCode || "",
        phoneNumber: initialData.phoneNumber || "",
        deliveryCenterId: initialData.deliveryCenterId || "",
      });
    } else {
      setFormData({
        countryCode: "55",
        areaCode: "",
        phoneNumber: "",
        deliveryCenterId: "",
      });
    }
    setFormErrors({});
    setError("");
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;

    if (["countryCode", "areaCode", "phoneNumber"].includes(field)) {
      const numericValue = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [field]: numericValue }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }

    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.countryCode.trim()) {
      errors.countryCode = "Codigo do pais e obrigatorio";
    } else if (formData.countryCode.length > 5) {
      errors.countryCode = "Codigo do pais deve ter no maximo 5 digitos";
    }

    if (!formData.areaCode.trim()) {
      errors.areaCode = "Codigo de area e obrigatorio";
    } else if (formData.areaCode.length > 5) {
      errors.areaCode = "Codigo de area deve ter no maximo 5 digitos";
    }

    if (!formData.phoneNumber.trim()) {
      errors.phoneNumber = "Numero de telefone e obrigatorio";
    } else if (formData.phoneNumber.length > 20) {
      errors.phoneNumber = "Numero de telefone deve ter no maximo 20 digitos";
    } else if (formData.phoneNumber.length < 8) {
      errors.phoneNumber = "Numero de telefone deve ter pelo menos 8 digitos";
    }

    if (!formData.deliveryCenterId) {
      errors.deliveryCenterId = "Centro de distribuicao e obrigatorio";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError("");

    try {
      const submitData = {
        country_code: `+${formData.countryCode}`,
        area_code: formData.areaCode,
        phone_number: formData.phoneNumber,
        delivery_center: formData.deliveryCenterId,
      };

      if (editMode) {
        await DeliveryCenterCostsService.updateBotPhoneNumber(
          initialData.id,
          submitData
        );
        onSuccess("Numero de telefone atualizado com sucesso!");
      } else {
        await DeliveryCenterCostsService.createBotPhoneNumber(submitData);
        onSuccess("Numero de telefone adicionado com sucesso!");
      }
    } catch (error) {
      console.error("Erro ao salvar numero de telefone:", error);
      setError(
        error.response?.data?.message ||
          "Falha ao salvar numero de telefone. Por favor, tente novamente."
      );
    } finally {
      setLoading(false);
    }
  };

  const commonCountryCodes = [
    { value: "55", label: "+55 (Brasil)" },
    { value: "1", label: "+1 (EUA/Canada)" },
    { value: "54", label: "+54 (Argentina)" },
    { value: "56", label: "+56 (Chile)" },
    { value: "57", label: "+57 (Colombia)" },
    { value: "51", label: "+51 (Peru)" },
  ];

  const getPhonePreview = () => {
    const { countryCode, areaCode, phoneNumber } = formData;
    if (countryCode && areaCode && phoneNumber) {
      return `+${countryCode} (${areaCode}) ${phoneNumber}`;
    }
    return "---";
  };

  const hasPreview =
    formData.countryCode && formData.areaCode && formData.phoneNumber;

  // Shared field styling
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      "&:hover fieldset": { borderColor: alpha(ACCENT, 0.4) },
      "&.Mui-focused fieldset": { borderColor: ACCENT },
    },
    "& .MuiInputLabel-root.Mui-focused": { color: ACCENT },
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      // Render above the AnimatedModal (z-index 5000 / 9999) that hosts this
      // form when it's opened from inside the delivery-center config modal;
      // MUI's default Dialog z-index (1300) would otherwise sit behind it.
      sx={{ zIndex: 11000 }}
      PaperProps={{
        component: motion.div,
        initial: { opacity: 0, scale: 0.95, y: 10 },
        animate: { opacity: 1, scale: 1, y: 0 },
        exit: { opacity: 0, scale: 0.95, y: 10 },
        transition: { duration: 0.25 },
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: `linear-gradient(135deg, ${ACCENT_BG} 0%, ${ACCENT_DARK} 50%, ${ACCENT} 100%)`,
          color: "#fff",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha("#fff", 0.15),
              backdropFilter: "blur(8px)",
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 20, color: "#25D366" }} />
          </Box>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: "0.95rem",
                lineHeight: 1.3,
                color: "#ffffff",
              }}
            >
              {editMode ? "Editar Numero" : "Novo Numero WhatsApp"}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: alpha("#fff", 0.55), fontSize: "0.68rem" }}
            >
              {editMode
                ? "Atualize os dados do numero"
                : "Cadastre um numero autorizado"}
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={onClose}
          sx={{
            minWidth: 0,
            p: 0.75,
            color: alpha("#fff", 0.6),
            borderRadius: 1.5,
            "&:hover": {
              backgroundColor: alpha("#fff", 0.1),
              color: "#fff",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2.5, borderRadius: 2 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Phone Number Preview */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              textAlign: "center",
              background: alpha(ACCENT, 0.04),
              border: `1px solid ${alpha(ACCENT, 0.12)}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <PhoneIcon
                sx={{ fontSize: 14, color: theme.palette.text.disabled }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: "0.68rem",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                Preview do Numero
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                color: hasPreview ? ACCENT_DARK : theme.palette.text.disabled,
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "0.5px",
              }}
            >
              {getPhonePreview()}
            </Typography>
          </Box>

          {/* Country Code */}
          <FormControl
            fullWidth
            error={!!formErrors.countryCode}
            variant="outlined"
            size="small"
            sx={fieldSx}
          >
            <InputLabel>Codigo do Pais</InputLabel>
            <Select
              value={formData.countryCode}
              onChange={handleInputChange("countryCode")}
              label="Codigo do Pais"
              sx={{ borderRadius: 2 }}
              // Dropdown menu portals to a separate layer; lift it above the
              // dialog (z 11000) so options aren't hidden behind it.
              MenuProps={{ sx: { zIndex: 11100 } }}
            >
              {commonCountryCodes.map((country) => (
                <MenuItem key={country.value} value={country.value}>
                  {country.label}
                </MenuItem>
              ))}
            </Select>
            {formErrors.countryCode && (
              <FormHelperText>{formErrors.countryCode}</FormHelperText>
            )}
          </FormControl>

          {/* Area Code + Phone in a row */}
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              label="Codigo de Area"
              value={formData.areaCode}
              onChange={handleInputChange("areaCode")}
              error={!!formErrors.areaCode}
              helperText={formErrors.areaCode || "Ex: 11, 21, 47"}
              inputProps={{ maxLength: 5 }}
              placeholder="11"
              size="small"
              sx={{ ...fieldSx, width: "35%" }}
            />

            <TextField
              fullWidth
              label="Numero de Telefone"
              value={formData.phoneNumber}
              onChange={handleInputChange("phoneNumber")}
              error={!!formErrors.phoneNumber}
              helperText={
                formErrors.phoneNumber || "Apenas numeros, sem formatacao"
              }
              inputProps={{ maxLength: 20 }}
              placeholder="999999999"
              size="small"
              sx={fieldSx}
            />
          </Box>

          {/* Delivery Center */}
          <FormControl
            fullWidth
            error={!!formErrors.deliveryCenterId}
            variant="outlined"
            size="small"
            sx={fieldSx}
          >
            <InputLabel>Centro de Distribuicao</InputLabel>
            <Select
              value={formData.deliveryCenterId}
              onChange={handleInputChange("deliveryCenterId")}
              label="Centro de Distribuicao"
              sx={{ borderRadius: 2 }}
              // Dropdown menu portals to a separate layer; lift it above the
              // dialog (z 11000) so options aren't hidden behind it.
              MenuProps={{ sx: { zIndex: 11100 } }}
            >
              {deliveryCenters.map((center) => (
                <MenuItem key={center.center_id} value={center.center_id}>
                  {center.center_name?.trim() || "Centro de Operação"}
                </MenuItem>
              ))}
            </Select>
            {formErrors.deliveryCenterId && (
              <FormHelperText>{formErrors.deliveryCenterId}</FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          background: alpha(theme.palette.grey[50], 0.5),
        }}
      >
        <Button
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: theme.palette.text.secondary,
            borderRadius: 2,
          }}
        >
          Cancelar
        </Button>
        <LoadingButton
          onClick={handleSubmit}
          loading={loading}
          variant="contained"
          startIcon={<SaveIcon />}
          loadingPosition="start"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            background: `linear-gradient(135deg, ${ACCENT_DARK} 0%, ${ACCENT} 100%)`,
            boxShadow: `0 2px 8px ${alpha(ACCENT, 0.3)}`,
            "&:hover": {
              boxShadow: `0 4px 14px ${alpha(ACCENT, 0.4)}`,
            },
          }}
        >
          {editMode ? "Atualizar" : "Adicionar"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsAppPhoneModal;
